import { FC, PropsWithChildren, ReactNode, useLayoutEffect, useState } from 'react';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { cn } from '@/utils/classNames.ts';
import Portal from '@/components/shared/Portal/Portal.tsx';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Button } from 'react-daisyui';
import { ModalProvider, useModalContext } from '@/components/modals/Modal/ModalContext.tsx';

type ModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  showCloseButton?: boolean;
  closeable?: boolean;
};

const ModalWrapper: FC<PropsWithChildren<PropsWithClassName<ModalProps>>> = ({ children, className, ...rest }) => {
  return (
    <ModalProvider {...rest}>
      <Modal className={className}>{children}</Modal>
    </ModalProvider>
  );
};

const Modal: FC<PropsWithChildren<PropsWithClassName>> = ({ children, className }) => {
  const { modalRef, modalId, titleId, handleClose, showCloseButton, closeable, navId } = useModalContext();

  return (
    <dialog ref={modalRef} id={modalId} className="modal">
      <div className={cn('modal-box relative pb-0 pt-0 bg-base-100')}>
        <div className="bg-base-100 sticky right-4 top-0 pt-6 pb-4 flex justify-between items-start gap-4 z-10">
          <div id={titleId}></div>
          {showCloseButton && (
            <Button onClick={handleClose} color="ghost" shape="circle" size="xs">
              <XMarkIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
        <div className={cn('max-w-full', className)}>{children}</div>
        <div
          className="flex gap-4 pt-4 pb-6 justify-between flex-wrap sticky bottom-0 bg-base-100 empty:pt-0"
          id={navId}
        ></div>
      </div>

      <div className="modal-backdrop">
        <button onClick={closeable ? handleClose : undefined}>close</button>
      </div>
    </dialog>
  );
};

type ModalNavProps = {
  children: (props: ReturnType<typeof useModalContext>) => ReactNode;
};
const ModalNav: FC<ModalNavProps> = ({ children }) => {
  const { navId, ...rest } = useModalContext();
  const [watch, setWatch] = useState(false);

  useLayoutEffect(() => {
    setWatch((prev) => !prev);
  }, []);

  return (
    <Portal watch={watch} elementId={navId}>
      {children({ navId, ...rest })}
    </Portal>
  );
};

type ModalTitleProps = {
  render?: (props: ReturnType<typeof useModalContext>) => ReactNode;
};
const ModalTitle: FC<PropsWithChildren<ModalTitleProps>> = ({ children, render }) => {
  const { titleId, ...rest } = useModalContext();
  const [watch, setWatch] = useState(false);

  useLayoutEffect(() => {
    setWatch((prev) => !prev);
  }, []);

  return (
    <Portal watch={watch} elementId={titleId}>
      {children}
      {render ? render({ titleId, ...rest }) : null}
    </Portal>
  );
};

export default {
  Wrapper: ModalWrapper,
  Nav: ModalNav,
  Title: ModalTitle,
};
